import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ColDef,
  themeQuartz,
  GridReadyEvent,
  colorSchemeLightWarm,
  colorSchemeDarkBlue,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  GridApi,
  SizeColumnsToContentStrategy,
  DefaultMenuItem,
  MenuItemDef,
  SizeColumnsToFitGridStrategy,
  ColGroupDef,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-enterprise";
import useTheme from "src/hooks/useTheme";
import { AgGridReact } from "ag-grid-react";
import { ReportInputs } from "src/models/ReportInputs";

type BaseGridProps<T> = {
  colDefs: ColDef<T>[] | ColGroupDef<T>[];
  defaultColDef: ColDef;
  getData?: (params: IServerSideGetRowsParams | null) => void;
  cacheBlockSize?: number;
  maxBlocksInCache?: number;
  isGridReady: boolean;
  reportInputs: ReportInputs;
  dataSource?: IServerSideDatasource;
  gridApi?: GridApi<any | undefined>;
  setGridApi: React.Dispatch<React.SetStateAction<GridApi<any> | undefined>>;
  getContextMenuItems: () => (DefaultMenuItem | MenuItemDef<any, any>)[];
  autoSizeStrategy:
    | SizeColumnsToContentStrategy
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy;
};

const BaseGrid = <T,>({
  colDefs,
  defaultColDef,
  getData,
  cacheBlockSize,
  maxBlocksInCache,
  isGridReady,
  reportInputs,
  dataSource,
  gridApi,
  setGridApi,
  getContextMenuItems,
  autoSizeStrategy,
}: BaseGridProps<T>) => {
  const { theme } = useTheme();
  const gridRef = useRef<AgGridReact>(null);
  const [gridReady, setGridReady] = useState(false);
  const paginationPageSizeSelector = useMemo(() => [5, 10, 25, 50, 100], []);
  const chartThemes = useMemo(
    () => (theme === "dark" ? ["ag-material-dark"] : ["ag-material"]),
    [theme]
  );

  useEffect(() => {
    if (isGridReady) setGridReady(true);
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.setGridOption("serverSideDatasource", dataSource);
    }
  }, [isGridReady, dataSource]);

  const onGridReadyHandler = useCallback(
    (params: GridReadyEvent) => {
      const gridApi = params.api;
      setGridApi(gridApi);
      const dataSource = createServerSideDatasource();
      if (!gridApi.isDestroyed())
        gridApi.setGridOption("serverSideDatasource", dataSource);
    },
    [reportInputs]
  );

  const gridTheme = useMemo(() => {
    return themeQuartz
      .withParams({
        spacing: 4,
      })
      .withPart(theme === "dark" ? colorSchemeDarkBlue : colorSchemeLightWarm);
  }, [theme]);  

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getData!(params);
      },
    };
  };

  return (
    <>
      {gridReady && (
        <div className="grid-container mt-4 me-2">
          <div>
            <AgGridReact
              ref={gridRef}
              enableCharts={true}
              cellSelection={true}
              theme={gridTheme}
              chartThemes={chartThemes}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={25}
              domLayout="autoHeight"
              paginationPageSizeSelector={paginationPageSizeSelector}
              onGridReady={onGridReadyHandler}
              rowModelType="serverSide"
              cacheBlockSize={cacheBlockSize ?? 100}
              maxBlocksInCache={maxBlocksInCache ?? 10}
              autoSizeStrategy={autoSizeStrategy}
              suppressMultiSort={true}
              getContextMenuItems={getContextMenuItems}
              debug={false}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default BaseGrid;
